import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useAdminData } from '../../hooks/useAdminData';
import {
  ShieldCheck, ShieldAlert, LogOut, RefreshCw, Search, Users, Activity,
  TrendingUp, AlertCircle, Wallet, UserMinus, XCircle, Key, Menu, X, Plus, Minus
} from 'lucide-react';

import StatusBadge from '../../components/StatusBadge';
import StatCard from '../../components/StatCard';
import RejectionModal from '../../components/RejectionModal';
import RecentActivity from '../../components/RecentActivity';
import JobApprovals from '../../components/JobApprovals';

// Restored utility function
const getBillableTime = (billableMinutes: number) => {
  if (!billableMinutes || billableMinutes === 0) return '0m billable';
  if (billableMinutes < 60) return `${billableMinutes}m billable`;
  const hours = Math.floor(billableMinutes / 60);
  const mins = billableMinutes % 60;
  return `${hours}h ${mins}m billable`;
};

const AdminDashboard = () => {
  const { user: adminUser, logout } = useAuth();
  const { tutors, students, loading, fetchAllData } = useAdminData();

  const [activeTab, setActiveTab] = useState<'TUTORS' | 'STUDENTS' | 'PAYMENTS' | 'LIVE_ROOMS' | 'APPLICATIONS'>('TUTORS');
  const [tutorFilter, setTutorFilter] = useState<'ALL' | 'PENDING'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [globalCommission, setGlobalCommission] = useState(30);

  const [systemRevenue, setSystemRevenue] = useState(0);
  const [auditLogs, setAuditLogs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [liveRooms, setLiveRooms] = useState<any[]>([]);
  const [pendingApplications, setPendingApplications] = useState([]);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    fetchAllData();

    try {
      const [logRes, payRes, liveRes, appRes, revRes] = await Promise.all([
        api.get('/admin/audit-logs'),
        api.get('/admin/transactions'),
        api.get('/admin/classrooms/active'),
        api.get('/admin/applications/pending'),
        api.get('/admin/revenue/total')
      ]);

      setAuditLogs(logRes.data);
      setTransactions(payRes.data);
      setLiveRooms(liveRes.data);
      setPendingApplications(appRes.data);
      setSystemRevenue(revRes.data.totalAmount || 0);

    } catch (err) {
      console.error("❌ Master Dashboard Fetch Failed:", err);
    }
  }, [fetchAllData]);

  const handleRejectionSubmit = async (reason: string) => {
    if (!selectedTutor) return;
    setIsSubmitting(true);
    try {
      await api.patch(`/admin/verify-tutor/${selectedTutor.id}`, { isVerified: false, reason });
      setShowRejectModal(false);
      setSelectedTutor(null);
      fetchDashboardData();
    } catch (err) {
      alert("Rejection failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualWalletAdjustment = async (userId: string, userName: string) => {
    const amountInput = window.prompt(`Manual Balance Adjustment for ${userName}:\nEnter amount (e.g. 100 to add, -50 to deduct):`);
    if (amountInput === null) return;

    const numericAmount = parseFloat(amountInput);
    if (isNaN(numericAmount) || numericAmount === 0) {
      return alert("Invalid amount entered.");
    }

    const reason = window.prompt("Enter reason for this adjustment (optional):", "Admin manual correction") || "Admin adjustment";

    try {
      setIsSubmitting(true);
      const res = await api.post('/admin/users/adjust-wallet', {
        userId,
        amount: numericAmount,
        reason
      });
      alert(`✅ Wallet adjusted! New Balance: ${res.data.newBalance} ETB`);
      fetchDashboardData();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to adjust wallet balance.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdminPasswordReset = async (userId: string, userName: string) => {
    const customPassword = window.prompt(`Enter a new temporary password for ${userName}:`);
    if (customPassword === null) return;

    if (customPassword.trim().length < 6) {
      return alert("Password must be at least 6 characters long.");
    }

    try {
      await api.post('/admin/users/reset-password', {
        userId,
        newPassword: customPassword.trim()
      });
      alert(`✅ Password override verified for ${userName}.`);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to update account credentials.");
    }
  };

  // Restored handleSetTutorRate function
  const handleSetTutorRate = async (tutorId: string) => {
    const rate = window.prompt("Enter custom commission % for this tutor (e.g., 20). Leave empty to reset to default.");
    if (rate === null) return;

    try {
      const numericRate = rate === "" ? 0 : parseFloat(rate);
      await api.patch(`/admin/tutors/${tutorId}/custom-rate`, { customRate: numericRate });
      alert("Tutor rate updated successfully!");
      fetchDashboardData(); 
    } catch (err) {
      alert("Error updating tutor rate.");
    }
  };

  const handleUpdateCommission = async () => {
    try {
      setIsSubmitting(true);
      await api.patch('/admin/settings/update', { defaultCommissionRate: globalCommission });
      alert(`Global commission updated to ${globalCommission}%`);
    } catch (err) {
      alert("Failed to update settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveApplication = async (applicationId: string, jobId: string) => {
    if (isSubmitting) return;

    const hourlyRateInput = window.prompt("Enter the agreed Hourly Rate (ETB):", "30");
    if (hourlyRateInput === null) return;

    const numericRate = parseFloat(hourlyRateInput);
    if (isNaN(numericRate) || numericRate <= 0) {
      return alert("Please enter a valid numeric rate.");
    }

    const subjectCommInput = window.prompt("Enter Subject Commission % (e.g., 40). Leave blank for default.");

    try {
      setIsSubmitting(true);

      if (subjectCommInput && subjectCommInput.trim() !== "") {
        const cleanValue = subjectCommInput.replace('%', '').trim();
        let jobComm = parseFloat(cleanValue);
        if (jobComm >= 1) jobComm = jobComm / 100;
        if (!isNaN(jobComm)) {
          await api.patch(`/admin/jobs/${jobId}/rate`, { commissionRate: jobComm });
        }
      }

      const approveRes = await api.post('/admin/applications/approve', { applicationId });
      const newSessionId = approveRes.data.sessionId;

      if (!newSessionId) throw new Error("Session ID missing from server response.");

      await api.patch('/admin/sessions/activate', { roomId: newSessionId, hourlyRate: numericRate });
      await fetchDashboardData();
      alert(`✅ Session Active at ${numericRate} ETB/hr`);

    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to activate session.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectApplication = async (applicationId: string) => {
    if (!window.confirm("Reject this tutor's application?")) return;
    try {
      await api.post('/admin/applications/reject', { applicationId });
      fetchDashboardData();
    } catch (err) {
      alert("Failed to reject application");
    }
  };

  const handleTerminateSession = async (roomId: string) => {
    if (!window.confirm("CRITICAL: Terminate this session and stop all billing immediately?")) return;
    try {
      setIsSubmitting(true);
      await api.post('/sessions/end', { roomId });
      await fetchDashboardData();
      alert("Session terminated.");
    } catch (err) {
      alert("Failed to terminate session.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    let interval: NodeJS.Timeout | null = null;
    if (activeTab === 'LIVE_ROOMS') {
      interval = setInterval(fetchDashboardData, 30000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [activeTab, fetchDashboardData]);

  // Restored handleActivateSession function
  const handleActivateSession = async (roomId: string) => {
    const inputRate = window.prompt("Admin: Set hourly rate (ETB/hr):", "25");
    if (inputRate === null) return;
    const numericRate = parseFloat(inputRate);
    if (isNaN(numericRate) || numericRate <= 0) return alert("Invalid rate.");

    try {
      setIsSubmitting(true);
      await api.patch('/admin/sessions/activate', { roomId, hourlyRate: numericRate });
      await fetchDashboardData();
      alert(`✅ Session activated at ${numericRate} ETB/hr!`);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to activate session.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKick = async (roomId: string, studentId: string, studentName: string) => {
    if (!window.confirm(`Force remove ${studentName}?`)) return;
    try {
      await api.post('/admin/classrooms/kick', { roomId, studentId, studentName });
      fetchDashboardData();
    } catch (err) {
      alert("Failed to kick student.");
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    const action = currentStatus ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${action} this user?`)) return;
    try {
      await api.patch(`/admin/toggle-status/${userId}`, { isActive: !currentStatus });
      fetchDashboardData();
    } catch (err) {
      alert("Status update failed");
    }
  };

  const handleVerify = async (tutorId: string) => {
    if (!window.confirm("Approve this tutor's credentials?")) return;
    try {
      await api.patch(`/admin/verify-tutor/${tutorId}`, { isVerified: true });
      fetchDashboardData();
    } catch (err) {
      alert("Verification failed");
    }
  };

  const handleProcessTransaction = async (txId: string, action: 'APPROVE' | 'REJECT') => {
    if (!window.confirm(`Confirm: ${action} this transaction?`)) return;
    try {
      await api.patch(`/admin/approve-transaction/${txId}`, { action });
      fetchDashboardData();
    } catch (err: any) {
      alert(err.response?.data?.message || "Transaction processing failed");
    }
  };

  const filteredData = useMemo(() => {
    if (activeTab === 'PAYMENTS') {
      return transactions.filter((t: any) => {
        const user = t.user?.fullName || '';
        const ref = t.referenceNo || '';
        const method = t.method || '';
        return user.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ref.toLowerCase().includes(searchQuery.toLowerCase()) ||
          method.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    if (activeTab === 'LIVE_ROOMS') return liveRooms;

    let list = activeTab === 'TUTORS' ? tutors : students;
    if (activeTab === 'TUTORS' && tutorFilter === 'PENDING') {
      list = list.filter((t: any) => !t.isVerified);
    }

    return list.filter((item: any) => {
      const name = item.fullName || item.user?.fullName || item.tutor || '';
      const secondary = item.email || item.referenceNo || item.sessionId || '';
      return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        secondary.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [tutors, students, transactions, liveRooms, activeTab, tutorFilter, searchQuery]);

  const totalSystemEtb = tutors.reduce((a, c: any) => a + (c.walletBalance || 0), 0) +
    students.reduce((a, c: any) => a + (c.walletBalance || 0), 0) +
    systemRevenue;

  return (
    <div className="h-screen-safe flex flex-col bg-slate-900 overflow-hidden relative">
      {/* 🚀 RESPONSIVE HEADER */}
      <header className="bg-slate-900/95 backdrop-blur-xl border-b border-slate-800 text-white px-4 md:px-8 py-3 md:py-4 flex justify-between items-center shadow-lg sticky top-0 z-50">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="bg-blue-600 p-1.5 md:p-2 rounded-lg shadow-lg"><ShieldCheck size={20} className="md:w-6 md:h-6" /></div>
          <div>
            <h1 className="text-sm md:text-xl font-bold tracking-tight leading-none">TutorAtrons</h1>
            <span className="text-blue-400 text-[8px] md:text-[10px] uppercase font-black tracking-widest leading-none mt-1 block">SuperAdmin Console</span>
          </div>
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-6">
          <button onClick={fetchDashboardData} className="p-2 hover:bg-slate-800 rounded-full transition-all text-slate-400">
            <RefreshCw size={20} className={loading ? "animate-spin text-blue-400" : ""} />
          </button>
          <div className="text-right border-l border-slate-700 pl-6">
            <p className="text-sm font-bold">{adminUser?.fullName}</p>
            <button onClick={() => window.confirm("Logout?") && logout()} className="text-[10px] text-red-400 hover:text-red-300 font-black uppercase tracking-widest flex items-center gap-1 mt-0.5">
              <LogOut size={10} /> Logout
            </button>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <button onClick={fetchDashboardData} className="p-2 rounded-full text-slate-400 active:bg-slate-800">
            <RefreshCw size={16} className={loading ? "animate-spin text-blue-400" : ""} />
          </button>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-xl bg-slate-800 text-slate-300 border border-slate-700">
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* 🚀 MOBILE MENU OVERLAY */}
      {isMobileMenuOpen && (
        <div className="absolute top-[60px] left-0 right-0 bg-slate-900 border-b border-slate-800 p-4 z-40 md:hidden flex flex-col gap-4 shadow-2xl animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
            <div className="flex flex-col">
              <span className="text-xs text-slate-400 uppercase tracking-widest font-bold">Admin</span>
              <span className="text-sm text-white font-black">{adminUser?.fullName}</span>
            </div>
            <button onClick={() => window.confirm("Logout?") && logout()} className="p-2.5 bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 p-3 md:p-8 max-w-7xl mx-auto w-full overflow-y-auto custom-scrollbar pb-24 md:pb-8">
        
        {/* 🚀 RESPONSIVE STAT CARDS */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="col-span-2 lg:col-span-1">
            <StatCard label="Platform Profit" value={`${systemRevenue.toLocaleString()} ETB`} icon={<TrendingUp size={16} />} colorClass="bg-indigo-100 text-indigo-600 border border-indigo-200" />
          </div>
          <StatCard label="Live Sessions" value={liveRooms.length} icon={<Activity size={16} />} colorClass="bg-red-100 text-red-600" />
          <StatCard label="Active Users" value={tutors.length + students.length} icon={<Users size={16} />} colorClass="bg-green-100 text-green-600" />
          <StatCard label="Waitlist" value={tutors.filter((t: any) => !t.isVerified).length} icon={<AlertCircle size={16} />} colorClass="bg-orange-100 text-orange-600" />
          <StatCard label="Liquidity" value={`${totalSystemEtb.toLocaleString()} ETB`} icon={<Wallet size={16} />} colorClass="bg-blue-100 text-blue-600" />
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          
          {/* Main Data View */}
          <div className="flex-[3] w-full min-w-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4 md:mb-6">
              
              {/* 🚀 SCROLLABLE TABS FOR MOBILE */}
              <div className="w-full overflow-x-auto no-scrollbar pb-1">
                <div className="flex bg-gray-200/90 p-1 rounded-xl shadow-inner w-max md:w-auto">
                  {['TUTORS', 'STUDENTS', 'PAYMENTS', 'LIVE_ROOMS', 'APPLICATIONS'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab as any)}
                      className={`px-3 md:px-4 py-2 text-[10px] md:text-[11px] font-black uppercase tracking-tighter rounded-lg transition-all whitespace-nowrap ${activeTab === tab ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {tab.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Responsive Search */}
              <div className="relative w-full md:w-72 shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search records..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl outline-none text-xs shadow-sm focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden w-full">
              {activeTab === 'APPLICATIONS' ? (
                <JobApprovals applications={pendingApplications} onApprove={handleApproveApplication} onReject={handleRejectApplication} />
              ) : (
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full text-left border-collapse min-w-[700px]">
                    <thead className="bg-gray-50/50 border-b border-gray-200 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <tr>
                        <th className="px-4 md:px-6 py-4">{activeTab === 'PAYMENTS' ? 'User & Ref' : activeTab === 'LIVE_ROOMS' ? 'Tutor' : 'Identity'}</th>
                        <th className="px-4 md:px-6 py-4">{activeTab === 'PAYMENTS' ? 'Transaction Details' : activeTab === 'LIVE_ROOMS' ? 'Student & Details' : 'Stats & Details'}</th>
                        <th className="px-4 md:px-6 py-4 text-center">Status</th>
                        <th className="px-4 md:px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredData.length === 0 ? (
                        <tr><td colSpan={4} className="px-6 py-10 text-center text-gray-400 text-sm italic">No active records in this category.</td></tr>
                      ) : (
                        filteredData.map((item: any) => (
                          <tr key={item.id || item.sessionId} className="hover:bg-blue-50/20 transition-colors group">
                            <td className="px-4 md:px-6 py-4">
                              <div className="font-bold text-gray-900 leading-none mb-1 md:group-hover:text-blue-600 transition-colors text-xs md:text-sm">
                                {item.user?.fullName || item.fullName || item.tutor}
                              </div>
                              <div className="text-[9px] md:text-[10px] text-gray-400 font-medium leading-none truncate max-w-[150px] md:max-w-[200px]">
                                {item.referenceNo || item.email || `SID: ${item.sessionId?.slice(0, 8)}...`}
                              </div>
                            </td>

                            <td className="px-4 md:px-6 py-4 text-xs">
                              {activeTab === 'PAYMENTS' ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5 md:gap-2">
                                    <span className={`text-[8px] md:text-[9px] font-black uppercase px-2 py-0.5 rounded ${item.type === 'DEPOSIT' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                      {item.type || 'DEPOSIT'}
                                    </span>
                                    <span className="font-bold text-gray-700 uppercase truncate">{item.method}</span>
                                  </div>
                                  <div className="font-black text-slate-800 text-xs md:text-sm">{item.amount} ETB</div>
                                </div>
                              ) : activeTab === 'LIVE_ROOMS' ? (
                                <div className="flex flex-col gap-1">
                                  <div className="font-bold text-slate-700 flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs truncate">
                                    <Users size={12} className="text-blue-500 shrink-0" /> {item.student}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className={`text-[8px] md:text-[9px] font-black uppercase tracking-widest ${Number(item.hourlyRate) > 0 ? 'text-blue-500' : 'text-orange-500 italic'}`}>
                                      {Number(item.hourlyRate) > 0 ? `Rate: ${item.hourlyRate} ETB/hr` : "⚠️ Rate Not Set"}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <div className="font-bold text-gray-700 text-[11px] md:text-xs truncate max-w-[150px]">{item.university || (item.gradeLevel ? `Grade ${item.gradeLevel}` : 'N/A')}</div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <div className="font-black text-green-600 flex items-center gap-1 text-[11px] md:text-sm">
                                      {item.walletBalance?.toLocaleString() || 0} <span className="text-[8px] md:text-[9px]">ETB</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>

                            <td className="px-4 md:px-6 py-4 text-center">
                              <StatusBadge active={item.status === 'APPROVED' || item.status === 'ACTIVE' || item.isActive} />
                              {activeTab === 'PAYMENTS' && (
                                <span className={`block text-[8px] font-black uppercase mt-1 ${item.status === 'APPROVED' ? 'text-emerald-600' : item.status === 'REJECTED' ? 'text-red-500' : 'text-amber-500 animate-pulse'}`}>
                                  {item.status}
                                </span>
                              )}
                            </td>

                            <td className="px-4 md:px-6 py-4 text-right">
                              <div className="flex justify-end gap-1.5 md:gap-2 flex-wrap md:flex-nowrap">
                                {activeTab === 'PAYMENTS' ? (
                                  item.status === 'PENDING' ? (
                                    <>
                                      <button onClick={() => handleProcessTransaction(item.id, 'APPROVE')} className="bg-emerald-600 text-white px-2 md:px-3 py-1 md:py-1.5 rounded-lg md:rounded-xl text-[9px] md:text-[10px] font-black uppercase shadow-sm hover:bg-emerald-700 transition-all">Approve</button>
                                      <button onClick={() => handleProcessTransaction(item.id, 'REJECT')} className="bg-red-600 text-white px-2 md:px-3 py-1 md:py-1.5 rounded-lg md:rounded-xl text-[9px] md:text-[10px] font-black uppercase shadow-sm hover:bg-red-700 transition-all">Reject</button>
                                    </>
                                  ) : (
                                    <span className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase italic">Processed</span>
                                  )
                                ) : activeTab === 'LIVE_ROOMS' ? (
                                  <>
                                    <button onClick={() => handleKick(item.sessionId, item.studentId, item.student)} className="bg-orange-100 text-orange-600 p-1.5 md:p-2 rounded-lg md:rounded-xl hover:bg-orange-600 hover:text-white transition-all shadow-sm" title="Kick Student"><UserMinus size={16} className="md:w-[18px] md:h-[18px]" /></button>
                                    <button onClick={() => handleTerminateSession(item.sessionId)} className="bg-red-100 text-red-600 p-1.5 md:p-2 rounded-lg md:rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm" title="Terminate Session"><XCircle size={16} className="md:w-[18px] md:h-[18px]" /></button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleManualWalletAdjustment(item.id, item.fullName || item.user?.fullName)}
                                      className="p-1.5 md:p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg md:rounded-xl transition-all shadow-sm border border-transparent hover:border-emerald-100"
                                      title="Manual Wallet Adjustment"
                                    >
                                      <Wallet size={16} className="md:w-[18px] md:h-[18px]" />
                                    </button>

                                    <button
                                      onClick={() => handleAdminPasswordReset(item.id, item.fullName || item.user?.fullName)}
                                      className="p-1.5 md:p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg md:rounded-xl transition-all shadow-sm border border-transparent hover:border-amber-100"
                                      title="Force Reset Password"
                                    >
                                      <Key size={16} className="md:w-[18px] md:h-[18px]" />
                                    </button>

                                    <button onClick={() => handleToggleStatus(item.id, item.isActive)} className={`p-1.5 md:p-2 rounded-lg md:rounded-xl transition-all ${item.isActive ? 'text-red-400 hover:bg-red-50' : 'text-green-500 hover:bg-green-50'}`}>
                                      {item.isActive ? <ShieldAlert size={16} className="md:w-[18px] md:h-[18px]" /> : <ShieldCheck size={16} className="md:w-[18px] md:h-[18px]" />}
                                    </button>

                                    {activeTab === 'TUTORS' && !item.isVerified && (
                                      <div className="flex flex-col md:flex-row gap-1 md:gap-2">
                                        <button onClick={() => handleVerify(item.id)} className="bg-blue-600 text-white px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[9px] md:text-[10px] font-black uppercase hover:bg-blue-700 transition-all">Verify</button>
                                        <button
                                          onClick={() => { setSelectedTutor(item); setShowRejectModal(true); }}
                                          className="bg-red-600 text-white px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[9px] md:text-[10px] font-black uppercase hover:bg-red-700 transition-all"
                                        >
                                          Reject
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* 🚀 RESPONSIVE SIDEBAR */}
          <aside className="w-full lg:w-[300px] shrink-0 flex flex-col gap-4 md:gap-6 lg:sticky lg:top-4">
            <div className="bg-white p-4 md:p-6 rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 md:mb-4 flex items-center gap-2">
                <ShieldCheck size={14} className="text-blue-500" /> System Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[9px] font-black text-gray-500 uppercase tracking-tighter">Global Commission (%)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      value={globalCommission}
                      onChange={(e) => setGlobalCommission(Number(e.target.value))}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:border-blue-500 transition-colors"
                    />
                    <div className="bg-gray-100 px-3 py-2 rounded-xl text-gray-400 font-bold text-xs">%</div>
                  </div>
                </div>
                <button
                  onClick={handleUpdateCommission}
                  disabled={isSubmitting}
                  className="w-full bg-slate-900 text-white py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all flex justify-center items-center gap-2 shadow-sm active:scale-95 disabled:opacity-50"
                >
                  {isSubmitting ? <RefreshCw size={12} className="animate-spin" /> : 'Apply Changes'}
                </button>
              </div>
            </div>

            {/* Audit Logs */}
            <div className="hidden lg:block">
               <RecentActivity logs={auditLogs} />
            </div>
           
          </aside>
        </div>
      </main>

      <RejectionModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        userName={selectedTutor?.fullName || ''}
        isSubmitting={isSubmitting}
        onSubmit={handleRejectionSubmit}
      />
    </div>
  );
};

export default AdminDashboard;